import React from 'react';
import get from 'lodash.get';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { selectors as configSelectors, Configuration, Message } from 'services/configuration';
import { GroupEvent } from 'services/ticketing';
import { EventTiers } from './EventTiers';
import { Layout } from './Layout';
import { PackageViewProps } from '../types';

const PackageView: React.FC<PackageViewProps> = (props: PackageViewProps) => {
  const { t, i18n } = useTranslation();
  const { groupIndex, groupEvents, tierSelection, getImageUrl, updateGroupTier } = props;
  const config = useSelector(configSelectors.selectConfiguration) as Configuration;
  const messaging: Message = get(config, `messaging.${i18n.language}.packageTierPage`, {});
  const productLabel = get(config, `configuration.labels.product`);
  const singularProductLabel = get(productLabel, `singular.${i18n.language}`, t('commonText.product'));
  const pluralProductLabel = get(productLabel, `plural.${i18n.language}`, t('commonText.product_plural'));

  const [filterdGroup, setFilteredGroup] = React.useState<string[] | null>(null);

  React.useEffect(() => {
    if (groupIndex > -1) {
      const currentGroup = Object.keys(groupEvents).filter((item, index) => index === groupIndex);
      setFilteredGroup(currentGroup);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupIndex, groupEvents]);

  const getProductLabelToDisplay = (quantity: number) => {
    return quantity === 1 ? singularProductLabel : pluralProductLabel;
  };

  const getPackageSelectionHeader = () => {
    let selectionHeader = '';
    if (groupEvents) {
      const selectedGroup = groupEvents[Object.keys(groupEvents)[groupIndex]];
      if (selectedGroup) {
        if (selectedGroup.minEvents === selectedGroup.maxEvents) {
          selectionHeader = `${t('tierSelection.select')} ${selectedGroup.minEvents} ${getProductLabelToDisplay(
            selectedGroup.minEvents
          )}`;
        } else {
          if (selectedGroup.minEvents && selectedGroup.minEvents !== 1) {
            if (selectedGroup.maxEvents && selectedGroup.maxEvents < selectedGroup.events.length) {
              if (selectedGroup.maxEvents - selectedGroup.minEvents === 1) {
                selectionHeader = `${t('tierSelection.select')} ${selectedGroup.minEvents} ${t('tierSelection.or')}
                ${selectedGroup.maxEvents} ${getProductLabelToDisplay(selectedGroup.maxEvents)}`;
              } else {
                selectionHeader = `${t('tierSelection.select')} ${t('tierSelection.between')} ${selectedGroup.minEvents}
                ${t('tierSelection.and')} ${selectedGroup.maxEvents}
                ${getProductLabelToDisplay(selectedGroup.maxEvents)}`;
              }
            } else {
              selectionHeader = `${t('tierSelection.select')} ${selectedGroup.minEvents} ${t('tierSelection.ormore')}
              ${getProductLabelToDisplay(selectedGroup.minEvents)}`;
            }
          } else {
            if (selectedGroup.maxEvents && selectedGroup.maxEvents < selectedGroup.events.length) {
              selectionHeader = `${t('tierSelection.select')} ${t('tierSelection.upto')}
              ${selectedGroup.maxEvents} ${getProductLabelToDisplay(selectedGroup.maxEvents)}`;
            } else {
              selectionHeader = `${t('tierSelection.select')} ${getProductLabelToDisplay(selectedGroup.maxEvents)}`;
            }
          }
        }
      }
    }

    return selectionHeader;
  };

  return (
    <Layout
      headerText={(messaging && messaging.header) || t('tierSelection.packageTierHeader')}
      subHeaderText={(messaging && messaging.message) || getPackageSelectionHeader()}
      hasTickets={Boolean(groupEvents && Object.keys(groupEvents).length && filterdGroup && filterdGroup.length)}
      refreshModel={filterdGroup}
    >
      {filterdGroup &&
        filterdGroup.map((group) => {
          return groupEvents[group].events.map((groupEvent: GroupEvent, idx: number) => (
            <EventTiers
              key={idx}
              event={groupEvent}
              tiers={groupEvent.tiers}
              tierSelection={tierSelection}
              tierSelectionKey={groupEvent.id}
              getImageUrl={getImageUrl}
              onTileClick={(tier: string) => updateGroupTier(tier, groupEvent.id, groupEvent.date as string)}
            />
          ));
        })}
    </Layout>
  );
};

export default PackageView;
